/* ---------------------------------------------------------------------------
   CartAPI v2.4 (Oct 2026) - the ONE pipeline for every cart mutation.

   All writes pass through a single serialized promise chain; quantity
   changes/removals are BATCHED into one /cart/update.js request. Every
   request is sent keepalive, so navigation can't kill one in flight.
   PAGEHIDE FLUSH: if the shopper leaves the page while mutations are
   still QUEUED (not yet sent), they are fired instantly in one keepalive
   shot - adds combined into one /cart/add.js, changes into one
   /cart/update.js. Nothing the shopper did can ever be silently lost.
   --------------------------------------------------------------------------- */
(function () {
  'use strict';
  if (window.CartAPI) return;

  var chain = Promise.resolve();
  var inFlight = 0;
  var pendingQty = {};  /* 'v<id>'|'l<line>' -> {q, id, line}  (queued changes) */
  var queuedAdds = [];  /* [{items:[...]}] adds accepted but not yet started  */

  var SECTIONS = 'cart-drawer,cart-icon-bubble';

  function render(sections) {
    try {
      var d = document.querySelector('cart-drawer');
      if (d && sections && typeof d.renderContents === 'function') {
        d.renderContents({ sections: sections }, false);
      } else {
        document.dispatchEvent(new CustomEvent('cart-drawer:refresh'));
      }
    } catch (e) {
      document.dispatchEvent(new CustomEvent('cart-drawer:refresh'));
    }
    try {
      if (document.getElementById('main-cart-items')) {
        var ci = document.querySelector('cart-items');
        if (ci && ci.onCartUpdate) ci.onCartUpdate();
        fetch(((window.routes && routes.cart_url) || '/cart') + '?section_id=main-cart-footer')
          .then(function (r) { return r.text(); })
          .then(function (t) {
            var doc = new DOMParser().parseFromString(t, 'text/html');
            var src = doc.querySelector('#main-cart-footer .js-contents');
            var tgt = document.querySelector('#main-cart-footer .js-contents');
            if (src && tgt) tgt.innerHTML = src.innerHTML;
          })
          .catch(function () {});
      }
    } catch (e) {}
    try {
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items' });
      }
    } catch (e) {}
  }

  function refresh() {
    document.dispatchEvent(new CustomEvent('cart-drawer:refresh'));
  }

  function run(task) {
    inFlight++;
    var p = chain.then(task, task);
    var settled = p.then(
      function (resp) {
        inFlight--;
        if (inFlight === 0 && resp !== undefined) {
          if (resp && resp.sections) render(resp.sections);
          else refresh();
        }
        return resp;
      },
      function (err) {
        inFlight--;
        if (inFlight === 0) refresh();
        throw err;
      }
    );
    chain = settled.then(function () {}, function () {});
    return settled;
  }

  function postJson(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      keepalive: true
    }).then(function (r) {
      return r.json().then(function (j) {
        if (!r.ok || (j && (j.status || j.errors))) {
          var e = new Error('cart request failed');
          e.data = j;
          throw e;
        }
        return j;
      });
    });
  }

  /* Page is being left: fire everything still queued, fire-and-forget. */
  function flushOnLeave() {
    try {
      var adds = [];
      queuedAdds.forEach(function (d) {
        if (!d.started && d.items) adds = adds.concat(d.items);
      });
      queuedAdds.length = 0;
      if (adds.length) {
        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: adds }),
          keepalive: true
        }).catch(function () {});
      }
      var updates = {}, any = false, lines = [];
      for (var k in pendingQty) {
        var e = pendingQty[k];
        if (e.id) { updates[String(e.id)] = e.q; any = true; }
        else lines.push(e);
        delete pendingQty[k];
      }
      if (any) {
        fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ updates: updates }),
          keepalive: true
        }).catch(function () {});
      }
      lines.forEach(function (e) {
        fetch((window.routes && routes.cart_change_url) || '/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ line: e.line, quantity: e.q }),
          keepalive: true
        }).catch(function () {});
      });
    } catch (err) {}
  }
  window.addEventListener('pagehide', flushOnLeave);
  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'hidden') flushOnLeave();
  });

  window.CartAPI = {
    /* payload: FormData from a product form, or {items:[{id,quantity}]} */
    add: function (payload) {
      var desc = null;
      if (!(payload instanceof FormData)) {
        desc = { items: payload.items, started: false };
        queuedAdds.push(desc);
      }
      return run(function () {
        if (desc) {
          desc.started = true;
          var i = queuedAdds.indexOf(desc);
          if (i > -1) queuedAdds.splice(i, 1);
        }
        if (payload instanceof FormData) {
          payload.set('sections', SECTIONS);
          payload.set('sections_url', location.pathname);
          var cfg = typeof fetchConfig === 'function' ? fetchConfig('javascript') : { headers: {} };
          var headers = Object.assign({}, cfg.headers, { 'X-Requested-With': 'XMLHttpRequest' });
          delete headers['Content-Type'];
          return fetch((window.routes && routes.cart_add_url) || '/cart/add', {
            method: 'POST', headers: headers, body: payload, keepalive: true
          }).then(function (r) {
            return r.json().then(function (j) {
              if (!r.ok || j.status) { var e = new Error('cart add failed'); e.data = j; throw e; }
              return j;
            });
          });
        }
        return postJson('/cart/add.js', {
          items: payload.items,
          sections: SECTIONS,
          sections_url: location.pathname
        });
      });
    },

    /* key: {k:'v<id>'|'l<line>', id:variantId|null, line:lineNumber} */
    change: function (key, quantity) {
      pendingQty[key.k] = { q: quantity, id: key.id, line: key.line };
      return run(function () {
        if (!(key.k in pendingQty)) return undefined; /* sent in an earlier batch */
        var updates = {}, lines = [], any = false;
        for (var k in pendingQty) {
          var e = pendingQty[k];
          if (e.id) { updates[String(e.id)] = e.q; any = true; }
          else lines.push(e);
          delete pendingQty[k];
        }
        var p = any
          ? postJson('/cart/update.js', { updates: updates, sections: SECTIONS, sections_url: location.pathname })
          : Promise.resolve(undefined);
        lines.forEach(function (e) {
          p = p.then(function () {
            return postJson((window.routes && routes.cart_change_url) || '/cart/change.js', {
              line: e.line, quantity: e.q, sections: SECTIONS, sections_url: location.pathname
            });
          });
        });
        return p;
      });
    },

    refresh: refresh,
    busy: function () { return inFlight > 0; }
  };
})();